/**
 * LaundryClassDetailPage - Detailansicht/Neuanlage einer Wäscheklasse
 * 
 * Pattern: LineDetailPage-Style
 * 
 * Create-Mode (/admin/laundry-classes/new):
 * - Name eingeben
 * - Code wird vom Server generiert
 * 
 * Edit-Mode (/admin/laundry-classes/:id):
 * - Grunddaten bearbeiten
 * - Code ist IMMUTABLE (read-only)
 * - Gefahrenzone für Deaktivierung
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layers, ArrowLeft, RefreshCw, Save, Trash2, AlertTriangle, Info } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';

interface LaundryClass {
  id: string;
  code: string;
  name: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
}

export default function LaundryClassDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [laundryClass, setLaundryClass] = useState<LaundryClass | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form State
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sortOrder, setSortOrder] = useState(0);

  // Delete Confirm State
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Load laundry class details (Edit-Mode)
  const loadLaundryClass = async () => {
    if (isNew || !id) return;
    try {
      const res = await fetch(`/api/admin/laundry-classes/${id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setLaundryClass(data);
        setName(data.name);
        setDescription(data.description || '');
        setSortOrder(data.sortOrder);
      } else if (res.status === 404) {
        navigate('/admin/laundry-classes');
      }
    } catch (err) {
      console.error('Fehler beim Laden der Wäscheklasse:', err);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      if (!isNew) {
        await loadLaundryClass();
      }
      setLoading(false);
    };
    init();
  }, [id, isNew]);

  // Save
  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name ist erforderlich');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (isNew) {
        // Create - Code wird vom Backend generiert
        const res = await fetch('/api/admin/laundry-classes', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            name: name.trim(),
            description: description.trim() || null,
            sortOrder,
          }),
        });

        if (res.ok) {
          const newClass = await res.json();
          navigate(`/admin/laundry-classes/${newClass.id}`);
        } else {
          const data = await res.json().catch(() => ({}));
          setError(data.error || data.message || 'Fehler beim Anlegen');
        }
      } else {
        // Edit-Mode: Wäscheklasse aktualisieren
        const res = await fetch(`/api/admin/laundry-classes/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            name: name.trim(),
            description: description.trim() || null,
            sortOrder,
          }),
        });

        if (res.ok) {
          navigate('/admin/laundry-classes');
        } else {
          const data = await res.json().catch(() => ({}));
          setError(data.error || data.message || 'Fehler beim Speichern');
        }
      }
    } catch (err) {
      setError('Netzwerkfehler');
    }
    setSaving(false);
  };

  // Delete (deactivate)
  const handleDelete = async () => {
    if (!id || isNew) return;

    setDeleting(true);
    try {
      const res = await fetch(`/api/admin/laundry-classes/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isActive: false }),
      });

      if (res.ok) {
        navigate('/admin/laundry-classes');
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Fehler beim Deaktivieren');
        setDeleteConfirmOpen(false);
      }
    } catch (err) {
      console.error('Fehler beim Deaktivieren:', err);
    }
    setDeleting(false);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Laden...</div>
      </AdminLayout>
    );
  }

  if (!isNew && !laundryClass) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Wäscheklasse nicht gefunden</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link
            to="/admin/laundry-classes"
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Layers className="w-6 h-6" />
              {isNew ? 'Neue Wäscheklasse' : laundryClass?.name}
            </h1>
            {!isNew && laundryClass && !laundryClass.isActive && (
              <p className="text-sm text-red-500 mt-0.5">(inaktiv)</p>
            )}
          </div>
          <div className="flex gap-2">
            {!isNew && (
              <button
                onClick={() => loadLaundryClass()}
                disabled={loading}
                className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                title="Aktualisieren"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving || !name.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Grunddaten */}
        <section className="bg-white rounded-lg border p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-900 border-b pb-2">Grunddaten</h2>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z.B. Hotelwäsche"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Beschreibung
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optionale Beschreibung"
              rows={2}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Sortierung */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Sortierung
            </label>
            <input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-slate-400 mt-1">
              Kleinere Zahlen erscheinen weiter oben in der Liste
            </p>
          </div>
        </section>

        {/* Hinweis */}
        {!isNew && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
            <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Hinweis zu historischen Daten</p>
              <p className="mt-1">
                Änderungen am Wäscheklassen-Stamm wirken sich nicht auf bereits erfasste Daten aus.
                Diese enthalten Snapshot-Daten zum Zeitpunkt der Erfassung.
              </p>
            </div>
          </div>
        )}

        {/* Delete Section (nur bei Edit und aktiv) */}
        {!isNew && laundryClass && laundryClass.isActive && (
          <section className="bg-white rounded-lg border border-red-200 p-6">
            <h2 className="font-semibold text-red-600 mb-2">Gefahrenzone</h2>
            <p className="text-sm text-slate-600 mb-4">
              Das Deaktivieren einer Wäscheklasse entfernt sie aus der Auswahl bei der Erfassung.
              Bereits erfasste Daten bleiben unverändert.
            </p>
            <button
              onClick={() => setDeleteConfirmOpen(true)}
              className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Wäscheklasse deaktivieren
            </button>
          </section>
        )}
      </div>

      {/* Delete Confirm Modal */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="p-4 border-b flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <h2 className="text-lg font-semibold text-red-600">Wäscheklasse deaktivieren?</h2>
            </div>
            <div className="p-4">
              <p className="text-slate-600">
                <strong>"{laundryClass?.name}"</strong> wird deaktiviert.
              </p>
              <p className="text-sm text-slate-500 mt-2">
                Die Wäscheklasse kann nicht mehr für neue Erfassungen verwendet werden.
              </p>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t bg-slate-50 rounded-b-xl">
              <button
                onClick={() => setDeleteConfirmOpen(false)}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deaktivieren...' : 'Deaktivieren'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
